import { CircularProgress, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
    useEffect, useRef, useState,
} from 'react';
import type { LoggedInUser } from '../../../../types/auth.types';
import {
    WHATSAPP_ENTRY_PURPOSES,
    WhatsAppEntryPurpose,
    WhatsAppEntryStatus,
} from '../../../../types/whatsappEntry.types';
import { getLoggedUser } from '../../../api/auth.api';
import {
    cancelWhatsAppEntry,
    getWhatsAppEntryStatus,
    linkWhatsAppEntry,
    loginWithWhatsAppEntry,
    logoutForWhatsAppAccountSwitch,
} from '../../../api/whatsappEntry.api';
import Alert from '../../foundation/Alert';
import Button from '../../foundation/Button';
import DialogWithHeader from '../../foundation/DialogWithHeader';
import LoginModal from '../../modals/LoginModal/LoginModal';
import RegisterModal from '../../modals/RegisterModal/RegisterModal';
import ResetPasswordModal from '../../modals/ResetPasswordModal/ResetPasswordModal';
import {
    EntryActions,
    EntryContent,
    EntryConnector,
    EntryConnectorBadge,
    EntryDialogHeader,
    EntryHeaderIcon,
    EntryHeaderTitle,
    EntryIdentityCard,
    EntryIdentityDetails,
    EntryIdentityFlow,
    EntryIdentityIcon,
    EntryIdentityLabel,
    EntryIdentityValue,
    EntryLoading,
    EntrySecurityNote,
    EntrySubtitle,
} from './WhatsAppEntryGate.styles';

type EntryPhase = 'closed' | 'loading' | 'action' | 'success' | 'error';
type AuthModal = 'login' | 'register' | 'resetPassword' | null;

export type WhatsAppEntryGateProps = {
    user?: LoggedInUser
    onUserChange?: (user?: LoggedInUser) => void
};

function removeTokenFromCurrentUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

function formatWhatsAppPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('972') && digits.length === 12) {
        return `+972 ${digits.slice(3, 5)}-${digits.slice(5, 8)}-${digits.slice(8)}`;
    }
    return digits ? `+${digits}` : phone;
}

function getSuccessText(purpose?: WhatsAppEntryPurpose): string {
    if (purpose === WHATSAPP_ENTRY_PURPOSES.JOB) {
        return 'החשבון קושר בהצלחה. אפשר להמשיך לצפות במשרה ולהגיש מועמדות כרגיל.';
    }
    if (purpose === WHATSAPP_ENTRY_PURPOSES.EXPRESS) {
        return 'החשבון קושר בהצלחה ושירות Mploy Express הופעל. אפשר להמשיך להעלאת קורות החיים או לחזור לשיחה.';
    }
    return 'החשבון קושר בהצלחה. אפשר לחזור לשיחה ולהמשיך את התהליך ב־WhatsApp.';
}

function WhatsAppEntryGate({ user, onUserChange }: WhatsAppEntryGateProps) {
    const [token, setToken] = useState<string | null>(null);
    const [status, setStatus] = useState<WhatsAppEntryStatus | null>(null);
    const [currentUser, setCurrentUser] = useState<LoggedInUser | undefined>(user);
    const [phase, setPhase] = useState<EntryPhase>('closed');
    const [authModal, setAuthModal] = useState<AuthModal>(null);
    const [showExitConfirmation, setShowExitConfirmation] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const onUserChangeRef = useRef(onUserChange);

    useEffect(() => {
        onUserChangeRef.current = onUserChange;
    }, [onUserChange]);

    useEffect(() => {
        setCurrentUser(user);
    }, [user]);

    useEffect(() => {
        let mounted = true;
        const entryToken = new URLSearchParams(window.location.search).get('token');
        if (!entryToken) return undefined;

        setToken(entryToken);
        setPhase('loading');

        const initialize = async () => {
            try {
                const entryStatus = await getWhatsAppEntryStatus(entryToken);
                if (!mounted) return;
                setStatus(entryStatus);

                if (entryStatus.canPasswordlessLogin && !entryStatus.accountConflict) {
                    const result = await loginWithWhatsAppEntry(entryToken);
                    if (!mounted) return;
                    setCurrentUser(result.user);
                    onUserChangeRef.current?.(result.user);
                    removeTokenFromCurrentUrl();
                    setToken(null);
                    if (entryStatus.purpose === WHATSAPP_ENTRY_PURPOSES.SMART_AGENT) {
                        setPhase('closed');
                        window.setTimeout(() => window.location.assign('/user/settings'), 0);
                        return;
                    }
                    if (entryStatus.purpose === WHATSAPP_ENTRY_PURPOSES.JOB) {
                        setPhase('closed');
                        window.setTimeout(() => window.location.reload(), 0);
                        return;
                    }
                    setPhase('closed');
                    return;
                }

                setPhase('action');
            } catch (error) {
                if (!mounted) return;
                setErrorMessage(error instanceof Error ? error.message : 'קישור ה־WhatsApp אינו תקין או שפג תוקפו.');
                setPhase('error');
            }
        };

        initialize();
        return () => {
            mounted = false;
        };
    }, []);

    const unresolved = !!token && (phase === 'loading' || phase === 'action');

    useEffect(() => {
        if (!unresolved) return undefined;
        const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeLeaving);
        return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
    }, [unresolved]);

    const finishEntry = (entryPurpose: WhatsAppEntryPurpose, linkedNow: boolean) => {
        removeTokenFromCurrentUrl();
        setToken(null);
        if (entryPurpose === WHATSAPP_ENTRY_PURPOSES.SMART_AGENT) {
            setPhase('closed');
            window.setTimeout(() => window.location.assign('/user/settings'), 0);
            return;
        }
        if (entryPurpose === WHATSAPP_ENTRY_PURPOSES.JOB && !linkedNow) {
            setPhase('closed');
            window.setTimeout(() => window.location.reload(), 0);
            return;
        }
        setPhase(linkedNow ? 'success' : 'closed');
    };

    const linkAuthenticatedUser = async (authenticatedUser: LoggedInUser) => {
        if (!token || !status) return;
        setAuthModal(null);
        setPhase('loading');
        setCurrentUser(authenticatedUser);
        onUserChangeRef.current?.(authenticatedUser);
        try {
            await linkWhatsAppEntry(token);
            try {
                const response = await getLoggedUser();
                setCurrentUser(response.data);
                onUserChangeRef.current?.(response.data);
            } catch {
                // The account is already linked; the authenticated user is a safe local fallback.
            }
            finishEntry(status.purpose, true);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'לא ניתן לקשר את החשבון.');
            setPhase('error');
        }
    };

    const useLinkedAccount = async () => {
        if (!token || !status) return;
        setPhase('loading');
        try {
            const result = await loginWithWhatsAppEntry(token, true);
            setCurrentUser(result.user);
            onUserChangeRef.current?.(result.user);
            finishEntry(status.purpose, false);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'לא ניתן לעבור לחשבון המקושר.');
            setPhase('error');
        }
    };

    const switchToAnotherAccount = async () => {
        setPhase('loading');
        try {
            await logoutForWhatsAppAccountSwitch();
            setCurrentUser(undefined);
            onUserChangeRef.current?.(undefined);
            setPhase('action');
        } catch {
            setErrorMessage('לא ניתן להתנתק מהחשבון הנוכחי. יש לנסות שוב.');
            setPhase('error');
        }
    };

    const requestClose = () => {
        if (unresolved) {
            setShowExitConfirmation(true);
            return;
        }
        removeTokenFromCurrentUrl();
        setToken(null);
        setPhase('closed');
    };

    const cancelAndClose = async () => {
        if (token) {
            try {
                await cancelWhatsAppEntry(token);
            } catch {
                // The local flow still closes; an unconsumed token expires after ten minutes.
            }
        }
        setShowExitConfirmation(false);
        removeTokenFromCurrentUrl();
        setToken(null);
        setPhase('closed');
    };

    const continueAfterSuccess = () => {
        if (status?.purpose === WHATSAPP_ENTRY_PURPOSES.EXPRESS) {
            setPhase('closed');
            return;
        }
        if (status?.purpose === WHATSAPP_ENTRY_PURPOSES.JOB) {
            window.location.reload();
            return;
        }
        setPhase('closed');
    };

    const returnToWhatsApp = () => {
        window.location.assign('/go/whatsapp');
    };

    const renderWhatsAppCard = () => {
        if (!status?.whatsappPhone) return null;
        return <EntryIdentityCard>
            <EntryIdentityIcon>
                <WhatsAppIcon />
            </EntryIdentityIcon>
            <EntryIdentityDetails>
                <EntryIdentityLabel variant="subtitle1">
                    <bdi dir="ltr">WhatsApp</bdi>
                </EntryIdentityLabel>
                <EntryIdentityValue variant="body1">
                    <bdi dir="ltr">{formatWhatsAppPhone(status.whatsappPhone)}</bdi>
                </EntryIdentityValue>
            </EntryIdentityDetails>
        </EntryIdentityCard>;
    };

    const renderAction = () => {
        if (status?.accountConflict) {
            return <>
                <EntrySubtitle variant="body1">
                    אחרי החיבור, אותו חשבון ישמש אותך באתר וב־<bdi dir="ltr">WhatsApp</bdi>.
                </EntrySubtitle>
                <EntryIdentityFlow>{renderWhatsAppCard()}</EntryIdentityFlow>
                <Alert severity="info">
                    מספר ה־WhatsApp הזה כבר מקושר לחשבון אחר. כדי להמשיך, יש לעבור לחשבון המקושר.
                </Alert>
                <EntryActions>
                    <Button color="primary" onClick={useLinkedAccount}>מעבר לחשבון המקושר</Button>
                </EntryActions>
            </>;
        }

        if (currentUser) {
            const accountLabel = currentUser.email || currentUser.username;
            return <>
                <EntrySubtitle variant="body1">
                    אחרי החיבור, אותו חשבון ישמש אותך באתר וב־<bdi dir="ltr">WhatsApp</bdi>.
                </EntrySubtitle>
                <EntryIdentityFlow>
                    {renderWhatsAppCard()}
                    <EntryConnector>
                        <EntryConnectorBadge><LinkIcon fontSize="small" /></EntryConnectorBadge>
                    </EntryConnector>
                    <EntryIdentityCard>
                        <EntryIdentityIcon $account>
                            <AccountCircleOutlinedIcon />
                        </EntryIdentityIcon>
                        <EntryIdentityDetails>
                            <EntryIdentityLabel variant="subtitle1">
                                חשבון <bdi dir="ltr">Mploy</bdi> המחובר כעת
                            </EntryIdentityLabel>
                            <EntryIdentityValue variant="body1">
                                <bdi dir="ltr">{accountLabel}</bdi>
                            </EntryIdentityValue>
                        </EntryIdentityDetails>
                    </EntryIdentityCard>
                </EntryIdentityFlow>
                <EntrySecurityNote>
                    <ShieldOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                        המשרות, ההעדפות והפעילות שלך יישמרו במקום אחד.
                    </Typography>
                </EntrySecurityNote>
                <EntryActions>
                    <Button color="primary" onClick={() => linkAuthenticatedUser(currentUser)}>
                        חיבור החשבונות
                    </Button>
                    <Button variant="outlined" onClick={switchToAnotherAccount}>
                        שימוש בחשבון אחר
                    </Button>
                </EntryActions>
            </>;
        }

        return <>
            <EntrySubtitle variant="body1">
                כדי לחבר את <bdi dir="ltr">WhatsApp</bdi> לחשבון <bdi dir="ltr">Mploy</bdi>,
                יש להתחבר או להירשם.
            </EntrySubtitle>
            <EntryIdentityFlow>{renderWhatsAppCard()}</EntryIdentityFlow>
            <EntryActions>
                <Button color="primary" onClick={() => setAuthModal('login')}>התחברות</Button>
                <Button variant="outlined" onClick={() => setAuthModal('register')}>הרשמה</Button>
            </EntryActions>
        </>;
    };

    if (phase === 'closed' && !authModal) return null;

    return <>
        <DialogWithHeader
            open={phase !== 'closed' && !authModal && !showExitConfirmation}
            onClose={requestClose}
            title={<EntryDialogHeader>
                <EntryHeaderIcon />
                <EntryHeaderTitle>
                    חיבור <bdi dir="ltr">WhatsApp</bdi>
                    <br />
                    לחשבון <bdi dir="ltr">Mploy</bdi>
                </EntryHeaderTitle>
            </EntryDialogHeader>}
            titleVariant="h5"
            fullWidthTitle
            dir="rtl"
            fullWidth
            maxWidth="md"
        >
            <EntryContent>
                {phase === 'loading' && <EntryLoading><CircularProgress /></EntryLoading>}
                {phase === 'action' && renderAction()}
                {phase === 'success' && <>
                    <Alert severity="success">{getSuccessText(status?.purpose)}</Alert>
                    <EntryActions>
                        <Button color="primary" onClick={continueAfterSuccess}>
                            {status?.purpose === WHATSAPP_ENTRY_PURPOSES.EXPRESS
                                ? 'המשך להעלאת קורות חיים'
                                : 'המשך'}
                        </Button>
                        {status?.purpose === WHATSAPP_ENTRY_PURPOSES.EXPRESS
                            ? <Button variant="outlined" onClick={returnToWhatsApp}>
                                חזרה לשיחה ב־WhatsApp
                            </Button>
                            : <Button variant="outlined" onClick={() => window.location.assign('/user/settings')}>
                                מעבר לאזור האישי
                            </Button>}
                    </EntryActions>
                </>}
                {phase === 'error' && <>
                    <Alert severity="error">{errorMessage || 'לא ניתן להשלים את קישור החשבון.'}</Alert>
                    <Button color="primary" onClick={requestClose}>סגירה</Button>
                </>}
            </EntryContent>
        </DialogWithHeader>

        <DialogWithHeader
            open={showExitConfirmation}
            onClose={() => setShowExitConfirmation(false)}
            title="ביטול חיבור החשבון"
            dir="rtl"
            firstActionText="להישאר בתהליך"
            onFirstActionClick={() => setShowExitConfirmation(false)}
            secondActionText="יציאה וביטול"
            onSecondActionClick={cancelAndClose}
            fullWidth
            maxWidth="sm"
        >
            <Typography>
                יציאה מהתהליך תבטל את הקישור הנוכחי לפעולות שביצעת דרך WhatsApp.
                אפשר יהיה להתחיל את התהליך מחדש מהשיחה.
            </Typography>
        </DialogWithHeader>

        <LoginModal
            open={authModal === 'login'}
            onClose={() => setAuthModal(null)}
            gotoRegister={() => setAuthModal('register')}
            onResetPasswordClick={() => setAuthModal('resetPassword')}
            onLoginComplete={linkAuthenticatedUser}
        />
        <RegisterModal
            open={authModal === 'register'}
            onClose={() => setAuthModal(null)}
            gotoLogin={() => setAuthModal('login')}
            onRegistrationComplete={linkAuthenticatedUser}
            showPhone={false}
            showSmartAgent={false}
        />
        <ResetPasswordModal
            open={authModal === 'resetPassword'}
            onClose={() => setAuthModal('login')}
        />
    </>;
}

export default WhatsAppEntryGate;
